import { z } from 'zod';
import { bounceMetricReadingSchema } from './jobs.js';
export var ReputationTemplateTypeEnum;
(function (ReputationTemplateTypeEnum) {
    ReputationTemplateTypeEnum["FEEDBACK_REQUEST"] = "feedback-request";
    ReputationTemplateTypeEnum["GOOGLE_REVIEW"] = "google-review";
    ReputationTemplateTypeEnum["FEEDBACK_REQUEST_REMINDER"] = "feedback-request-reminder";
    ReputationTemplateTypeEnum["GOOGLE_REVIEW_REMINDER"] = "google-review-reminder";
    ReputationTemplateTypeEnum["COMBINED_REQUEST"] = "combined-request";
    ReputationTemplateTypeEnum["COMBINED_REQUEST_REMINDER"] = "combined-request-reminder";
})(ReputationTemplateTypeEnum || (ReputationTemplateTypeEnum = {}));
/** Single source of truth for business automation statuses */
export var BusinessAutomationStatusEnum;
(function (BusinessAutomationStatusEnum) {
    BusinessAutomationStatusEnum["DRAFT"] = "draft";
    BusinessAutomationStatusEnum["ACTIVE"] = "active";
    BusinessAutomationStatusEnum["RUNNING"] = "running";
    BusinessAutomationStatusEnum["SENDING"] = "sending";
    BusinessAutomationStatusEnum["COMPLETED"] = "completed";
    BusinessAutomationStatusEnum["FAILED"] = "failed";
    BusinessAutomationStatusEnum["CANCELLED"] = "cancelled";
    BusinessAutomationStatusEnum["PARTIALLY_CANCELLED"] = "partially_cancelled";
    BusinessAutomationStatusEnum["ARCHIVED"] = "archived";
})(BusinessAutomationStatusEnum || (BusinessAutomationStatusEnum = {}));
/**
 * Statuses where a tenant has the automation enabled / actively running — the "In Use" set.
 * Distinct from the derived "Active" list-status tier (a template is Active when AT LEAST ONE tenant
 * is in one of these states). Single source of truth for both the backend status aggregation and the
 * frontend tenant-status mapper.
 *
 * Note: the frontend mapper also treats the legacy string `'scheduled'` as enabled, but that is not a
 * `BusinessAutomationStatusEnum` member (the backend never emits it), so it is not listed here.
 */
export const ENABLED_AUTOMATION_STATUSES = [
    BusinessAutomationStatusEnum.ACTIVE,
    BusinessAutomationStatusEnum.RUNNING,
    BusinessAutomationStatusEnum.SENDING,
];
/** Valid transitions for recurring automations (trigger-based, date-based). Used for backend validation and UI dropdowns. */
export const RECURRING_STATE_TRANSITIONS = {
    [BusinessAutomationStatusEnum.DRAFT]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.SENDING,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.SENDING]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.ACTIVE]: [
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.ARCHIVED]: [BusinessAutomationStatusEnum.DRAFT],
    [BusinessAutomationStatusEnum.RUNNING]: [],
    [BusinessAutomationStatusEnum.COMPLETED]: [],
    [BusinessAutomationStatusEnum.FAILED]: [],
    [BusinessAutomationStatusEnum.CANCELLED]: [],
    [BusinessAutomationStatusEnum.PARTIALLY_CANCELLED]: [],
};
/** Valid transitions for one-time automations (e.g. broadcasts). Backend validation. ARCHIVED is terminal. */
export const ONE_TIME_STATE_TRANSITIONS = {
    [BusinessAutomationStatusEnum.DRAFT]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.SENDING,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.SENDING]: [
        BusinessAutomationStatusEnum.ACTIVE,
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.PARTIALLY_CANCELLED,
    ],
    [BusinessAutomationStatusEnum.ACTIVE]: [
        BusinessAutomationStatusEnum.DRAFT,
        BusinessAutomationStatusEnum.RUNNING,
        BusinessAutomationStatusEnum.FAILED,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.PARTIALLY_CANCELLED,
    ],
    [BusinessAutomationStatusEnum.RUNNING]: [
        BusinessAutomationStatusEnum.COMPLETED,
        BusinessAutomationStatusEnum.FAILED,
        BusinessAutomationStatusEnum.CANCELLED,
        BusinessAutomationStatusEnum.PARTIALLY_CANCELLED,
    ],
    [BusinessAutomationStatusEnum.COMPLETED]: [
        BusinessAutomationStatusEnum.FAILED,
        BusinessAutomationStatusEnum.ARCHIVED,
    ],
    [BusinessAutomationStatusEnum.FAILED]: [BusinessAutomationStatusEnum.ARCHIVED],
    [BusinessAutomationStatusEnum.CANCELLED]: [BusinessAutomationStatusEnum.ARCHIVED],
    [BusinessAutomationStatusEnum.PARTIALLY_CANCELLED]: [BusinessAutomationStatusEnum.ARCHIVED],
    [BusinessAutomationStatusEnum.ARCHIVED]: [],
};
/** Human-readable labels for status dropdowns (API uses lowercase enum values). */
export const BUSINESS_AUTOMATION_STATUS_LABELS = {
    [BusinessAutomationStatusEnum.DRAFT]: 'Draft',
    [BusinessAutomationStatusEnum.ACTIVE]: 'Active',
    [BusinessAutomationStatusEnum.RUNNING]: 'Running',
    [BusinessAutomationStatusEnum.COMPLETED]: 'Completed',
    [BusinessAutomationStatusEnum.FAILED]: 'Failed',
    [BusinessAutomationStatusEnum.CANCELLED]: 'Cancelled',
    [BusinessAutomationStatusEnum.PARTIALLY_CANCELLED]: 'Partially Cancelled',
    [BusinessAutomationStatusEnum.SENDING]: 'Sending',
    [BusinessAutomationStatusEnum.ARCHIVED]: 'Archived',
};
/**
 * Returns status options valid for the UI dropdown: current status plus all allowed transitions.
 * Use the appropriate map based on trigger type so users only see options the backend will accept.
 * Safe to use in frontend; does not depend on @reach/database or @reach/common.
 */
export function getValidStatusOptionsForUi(currentStatus, triggerType) {
    const normalized = (currentStatus || BusinessAutomationStatusEnum.DRAFT).toLowerCase();
    const map = triggerType === BusinessAutomationTriggerTypeEnum.ONE_TIME
        ? ONE_TIME_STATE_TRANSITIONS
        : RECURRING_STATE_TRANSITIONS;
    // Map is keyed by enum but API passes string; enum values are strings at runtime
    const transitions = map[normalized];
    const options = new Set([normalized]);
    if (transitions) {
        for (const t of transitions)
            options.add(t);
    }
    return Array.from(options).map(value => ({
        value,
        label: BUSINESS_AUTOMATION_STATUS_LABELS[value] ?? value,
    }));
}
export var BusinessAutomationTriggerTypeEnum;
(function (BusinessAutomationTriggerTypeEnum) {
    /**
     * This is special, it means that the automation will only run once and then be completed
     */
    BusinessAutomationTriggerTypeEnum["ONE_TIME"] = "one_time";
    BusinessAutomationTriggerTypeEnum["TRIGGER_BASED"] = "trigger_based";
    BusinessAutomationTriggerTypeEnum["DATE_BASED"] = "date_based";
})(BusinessAutomationTriggerTypeEnum || (BusinessAutomationTriggerTypeEnum = {}));
export var BusinessAutomationActionTypeEnum;
(function (BusinessAutomationActionTypeEnum) {
    BusinessAutomationActionTypeEnum["SEND_COMMUNICATION"] = "send_communication";
    /**
     * Wait until the next available time today, or skip if the time has already passed
     *
     * Behavior:
     * - If target time (e.g. 9AM) is in the future today → wait until that time
     * - If target time has already passed today → skip the action (return null)
     * - Skip just means move on to the next action, this requirement is met
     *
     * This is different from WAIT_UNTIL_NEXT_OCCURRENCE which always waits for the next
     * occurrence, even if it means waiting until tomorrow.
     *
     * Example: If it's 11AM and target is 9AM, this action will be skipped.
     * Example: If it's 7AM and target is 9AM, this action will wait until 9AM today.
     */
    BusinessAutomationActionTypeEnum["WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME"] = "wait_until_next_fixed_time";
    /**
     * Wait until the next occurrence of a specific time, always waiting for the next day if time has passed
     * (e.g. if it's 8:01 AM and target is 8:00 AM, wait until 8:00 AM tomorrow)
     */
    BusinessAutomationActionTypeEnum["WAIT_UNTIL_NEXT_OCCURRENCE"] = "wait_until_next_occurrence";
    BusinessAutomationActionTypeEnum["WAIT_FOR_DURATION"] = "wait_for_duration";
    /**
     * Exits automation early if user is no longer in segment
     */
    BusinessAutomationActionTypeEnum["CHECK_SEGMENT_MEMBERSHIP"] = "check_segment_membership";
})(BusinessAutomationActionTypeEnum || (BusinessAutomationActionTypeEnum = {}));
/**
 * Maximum allowed total delay for a WAIT_FOR_DURATION action. Enforced on
 * automation create/update and on automation template create/update; the
 * partner dashboard mirrors this in the wait-step UI.
 */
export const MAX_AUTOMATION_DELAY_DAYS = 365;
export function calculateTotalDelayInDays(duration) {
    const { days = 0, hours = 0, minutes = 0, seconds = 0 } = duration;
    return days + hours / 24 + minutes / (24 * 60) + seconds / (24 * 60 * 60);
}
export var TimezoneEnum;
(function (TimezoneEnum) {
    TimezoneEnum["EST"] = "America/New_York";
})(TimezoneEnum || (TimezoneEnum = {}));
export var MonthDayTriggerTypeEnum;
(function (MonthDayTriggerTypeEnum) {
    MonthDayTriggerTypeEnum["FIXED_DATE"] = "fixed_date";
})(MonthDayTriggerTypeEnum || (MonthDayTriggerTypeEnum = {}));
export var EventFilterTypeEnum;
(function (EventFilterTypeEnum) {
    EventFilterTypeEnum["SEGMENT_ID"] = "segment_id";
})(EventFilterTypeEnum || (EventFilterTypeEnum = {}));
export var ArtifactOutputKeyEnum;
(function (ArtifactOutputKeyEnum) {
    ArtifactOutputKeyEnum["USER_ID"] = "userId";
})(ArtifactOutputKeyEnum || (ArtifactOutputKeyEnum = {}));
export const oneTimeTriggerMetadataSchema = z.object({
    triggerType: z.literal(BusinessAutomationTriggerTypeEnum.ONE_TIME),
    scheduledAt: z.union([
        z.coerce
            .date()
            .describe('String in ISO 8601 format with timezone information (e.g., "2024-03-20T15:00:00-07:00" or "2024-03-20T15:00:00Z")'),
        z.literal('now').describe('Schedule the automation to run 5 minutes from marking it as active'),
    ]),
    timezone: z
        .string()
        .optional()
        .describe('Timezone in IANA format (e.g., "America/New_York"). This is used for auditing and frontend display. Times are automatically converted to UTC when stored.'),
});
export const eventBasedTriggerMetadataSchema = z.object({
    triggerType: z.literal(BusinessAutomationTriggerTypeEnum.TRIGGER_BASED),
    /**
     * @see ReachEventNamesEnum for built-in event names
     */
    eventName: z.string(),
    /**
     * How to filter the event
     * For example if the event name is USER_ENTERS_SEGMENT, the filter should be the segment id
     * to specifically target a segment
     */
    eventFilter: z.record(z.union([z.string(), z.literal(EventFilterTypeEnum.SEGMENT_ID)]), z.string()),
    /**
     * This specifies the cooldown period in seconds. This translates to the amount of time
     * that must pass before a user can be targeted by this automation again
     */
    cooldownSeconds: z.number(),
    /**
     * These are the artifacts that will be passed to the first action
     * from the event data. For example if the event name is USER_ENTERS_SEGMENT, the artifact_output_keys
     * should be the EventFilterTypeEnum.SEGMENT_ID and ArtifactOutputKeyEnum.USER_ID
     */
    artifactOutputKeys: z.array(z.string()),
    triggerIdKey: z.string(),
    rootActionId: z.string(),
    customTriggerCopy: z.string().optional(),
});
const baseActionMetadataSchema = z.object({
    currentActionId: z.string().optional(),
    nextActionId: z.string().optional(),
    templateActionId: z.string().uuid().optional(),
});
export const sendCommunicationActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.SEND_COMMUNICATION),
    communicationGroupId: z.string(),
    customBlockText: z.string().optional(),
    reputationTemplateType: z.nativeEnum(ReputationTemplateTypeEnum).optional(),
});
export const waitUntilFixedTimeActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME),
    hour: z.number(),
    minute: z.number(),
    timezone: z.nativeEnum(TimezoneEnum),
});
export const waitUntilNextOccurrenceActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE),
    hour: z.number(),
    minute: z.number(),
    timezone: z.nativeEnum(TimezoneEnum),
});
export const delayByDurationActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.WAIT_FOR_DURATION),
    days: z.number().min(0).default(0),
    hours: z.number().min(0).default(0),
    minutes: z.number().min(0).default(0),
    seconds: z.number().min(0).default(0),
});
export const checkSegmentMembershipActionMetadataSchema = baseActionMetadataSchema.extend({
    actionType: z.literal(BusinessAutomationActionTypeEnum.CHECK_SEGMENT_MEMBERSHIP),
    segmentIds: z.array(z.string().uuid()),
});
export const actionMetadataSchema = z.discriminatedUnion('actionType', [
    sendCommunicationActionMetadataSchema,
    waitUntilFixedTimeActionMetadataSchema,
    waitUntilNextOccurrenceActionMetadataSchema,
    delayByDurationActionMetadataSchema,
    checkSegmentMembershipActionMetadataSchema,
]);
export const singleActionData = z.object({
    actionType: z.nativeEnum(BusinessAutomationActionTypeEnum),
    actionMetadata: actionMetadataSchema,
});
export const actionDataSchema = z.array(singleActionData);
export const dateBasedTriggerMetadataSchema = z.object({
    triggerType: z.literal(BusinessAutomationTriggerTypeEnum.DATE_BASED),
    monthDayTriggerType: z.nativeEnum(MonthDayTriggerTypeEnum),
    dayToTrigger: z.number().describe('Day of the month to trigger on'),
    monthToTrigger: z.number().describe('Month to trigger on'),
    customTriggerCopy: z
        .string()
        .optional()
        .describe('Optional copy to use when displaying in the frontend'),
    rootActionId: z.string(),
});
/** Closed union of the Campaign Performance tabs (Automations vs one-time Broadcasts). Single source of truth shared by the API query schema, common service, and the admin UI. */
export const campaignPerformanceTypeSchema = z.enum(['automations', 'broadcasts']);
/** One row of the Campaign Performance tables. Single source of truth for the API response, common service, and admin UI. Dates are ISO strings (the JSON wire format); rates are 0–1 or null when the denominator is zero. */
export const campaignPerformanceRowSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.nativeEnum(BusinessAutomationStatusEnum),
    triggerType: z.nativeEnum(BusinessAutomationTriggerTypeEnum),
    channels: z.array(z.enum(['email', 'sms'])),
    email: z.object({
        sent: z.number(),
        delivered: z.number(),
        openRate: z.number().nullable(),
        clicks: z.number(),
        bounceRate: z.number().nullable(),
        spamRate: z.number().nullable(),
        unsubRate: z.number().nullable(),
    }),
    sms: z.object({
        sent: z.number(),
        delivered: z.number(),
        failed: z.number(),
    }),
    sales: z.object({
        totalRevenue: z.number(),
        totalOrders: z.number(),
    }),
    scheduledAt: z.string().nullable().optional(),
    updatedAt: z.string().datetime().optional(),
});
export const hasBroadcastExperienceResponseSchema = z.object({
    hasBroadcastExperience: z
        .boolean()
        .describe('Whether the business has enough broadcast history to be past email best-practices guidance'),
});
export const businessAutomationSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    name: z.string(),
    status: z.nativeEnum(BusinessAutomationStatusEnum),
});
export const pausedAutomationSchema = businessAutomationSchema.extend({
    pausedAt: z.string().datetime(),
    lastReleaseAt: z.string().datetime().nullable(),
    pendingCount: z.number().int(),
    releasedCount: z.number().int(),
    resolvedCount: z.number().int(),
    hardBounces: z.number().int(),
    softBounces: z.number().int(),
    bounceMetrics: z.array(bounceMetricReadingSchema),
    hasActivePacingLoop: z.boolean(),
});
//# sourceMappingURL=business-automation.js.map